import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useRole } from '../app/RoleContext';
import api from '../lib/api';
import { PageHead, Section, Spinner, ErrorNote, useAsync } from '../components/ui';
import { GroupCard, EventCard, CourseCardView, PracticeCardView } from '../components/cards';
import InvitesSection from '../components/InvitesSection';

/**
 * Home — "my stuff" dashboard (design/024 reorder). In fixed order:
 *   1. My Invites (inline actionable cards, hidden when 0)
 *   2. My Events
 *   3. My Groups
 *   4. My Courses
 *   5. My Practices
 * Each "My" section, when empty, renders a "Browse X →" CTA instead of a
 * card grid. Sorted by updated_at desc within each section (server-side).
 * Discovery of un-enrolled content happens via the sidebar catalogs.
 */
export default function HomePage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const { effectiveRole } = useRole();

  const { loading, error, data, reload } = useAsync(async () => {
    const [courses, practices, groups, events, groupInvites, courseInvites, practiceInvites, eventInvites] = await Promise.all([
      api.myCourses().catch(() => []),
      api.myPractices().catch(() => []),
      api.myGroups().catch(() => []),
      api.myEvents().catch(() => []),
      api.listMyInvites().catch(() => []),
      api.listMyCourseInvites().catch(() => []),
      api.listMyPracticeInvites().catch(() => []),
      api.listMyEventInvites().catch(() => []),
    ]);
    return {
      courses: Array.isArray(courses) ? courses : [],
      practices: Array.isArray(practices) ? practices : [],
      groups: Array.isArray(groups) ? groups : [],
      events: Array.isArray(events) ? events : [],
      groupInvites: Array.isArray(groupInvites) ? groupInvites : [],
      courseInvites: Array.isArray(courseInvites) ? courseInvites : [],
      practiceInvites: Array.isArray(practiceInvites) ? practiceInvites : [],
      eventInvites: Array.isArray(eventInvites) ? eventInvites : [],
    };
  }, []);

  const name = me?.username && me.username !== me.email ? me.username : (me?.email || '');

  return (
    <div className="page">
      <PageHead
        icon="🏠"
        title={`Welcome back${name ? `, ${name}` : ''}`}
        sub={effectiveRole === 'super' ? '⚡ Signed in as superadmin' : undefined}
      />

      {loading ? <Spinner /> : error ? <ErrorNote error={error} onRetry={reload} /> : (
        <>
          <InvitesSection
            groupInvites={data.groupInvites}
            courseInvites={data.courseInvites}
            practiceInvites={data.practiceInvites}
            eventInvites={data.eventInvites}
            onChanged={reload}
          />

          {/* design/024 order: Events → Groups → Courses → Practices. */}
          {/* 1. My Events */}
          <MySection
            title="📅 My Events"
            empty="You don't have any upcoming events."
            browseLabel="View all events"
            onBrowse={() => navigate('/events')}
            items={data.events}
            renderGrid={(items) => (
              <div className="card-grid">
                {items.map((e) => <EventCard key={e.id} event={e} fromHome />)}
              </div>
            )}
          />

          {/* 2. My Groups */}
          <MySection
            title="👥 My Groups"
            empty="You're not a member of any groups yet."
            browseLabel="View all groups"
            onBrowse={() => navigate('/groups')}
            items={data.groups}
            renderGrid={(items) => (
              <div className="card-grid">
                {items.map((g) => <GroupCard key={g.id} group={g} fromHome />)}
              </div>
            )}
          />

          {/* 3. My Courses */}
          <MySection
            title="📚 My Courses"
            empty="You're not enrolled in any courses yet."
            browseLabel="View all courses"
            onBrowse={() => navigate('/courses')}
            items={data.courses}
            renderGrid={(items) => (
              <div className="card-grid">
                {items.map((c) => (
                  <CourseCardView key={c.id} c={c} hideStatusBadge onOpen={() => navigate(`/learn/course/${c.id}`, { state: { from: 'home' } })} />
                ))}
              </div>
            )}
          />

          {/* 4. My Practices */}
          <MySection
            title="🧘 My Practices"
            empty="You don't have any practices yet."
            browseLabel="View all practices"
            onBrowse={() => navigate('/practices')}
            items={data.practices}
            renderGrid={(items) => (
              <div className="card-grid">
                {items.map((p) => (
                  <PracticeCardView key={p.id} p={p} hideStatusBadge onOpen={() => navigate(`/learn/practice/${p.id}`, { state: { from: 'home' } })} />
                ))}
              </div>
            )}
          />
        </>
      )}
    </div>
  );
}

/**
 * One Home section: renders the card grid when there are items, or a single
 * "Browse X →" CTA button when the list is empty. Never shows the section
 * title on empty state so the CTA reads as a standalone next-action.
 */
function MySection({ title, empty, browseLabel, onBrowse, items, renderGrid }) {
  // Always show the "View all …" link in the section head so users can
  // reach the full catalog even when they already have items in "My …".
  const action = (
    <a onClick={onBrowse} style={{ cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, color: 'var(--indigo, #4f46e5)' }}>
      {browseLabel} →
    </a>
  );
  if (items.length === 0) {
    return (
      <Section title={title} action={action}>
        <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>{empty}</div>
      </Section>
    );
  }
  return <Section title={title} action={action}>{renderGrid(items)}</Section>;
}

// InvitesSection moved to ../components/InvitesSection — shared with the
// Notifications page so both surfaces render the same actionable list.
